import "./MarkdownRenderer.scss";

import DOMPurify from "dompurify";
// @ts-ignore
import Parser from "html-react-parser";
import marked, { Renderer } from "marked";
import { useMemo, useRef } from "react";
import { InlineMath } from "react-katex";

import { Text, Tooltip, useTheme } from "@seneca/senekit";

import { retainWhiteSpace } from "features/common/components/MarkdownRenderer/utils";

import { KATEX_CLASS, KEYWORDS } from "./consts";
import MarkdownReplacer from "./MarkdownReplacer";
import { removeMarkdownHeadings } from "./removeMarkdownHeadings";

/*

- If your content is on a single line, our markdown parser will convert this to a <p>
- If you add a new line, \n is added to the string, but a single <p> is still returned
- If you add 2+ new lines, multiple <p>s are returned

- We have two EQ markdown renderers: EQQuestionMarkdownRenderer and EQCaptionAndMarkSchemeMarkdownRenderer
- EQQuestionMarkdownRenderer is used in creation to preview questions
- EQCaptionAndMarkSchemeMarkdownRenderer is used in creation to preview captions & mark schemes

- The difference to note between them is that EQQuestionMarkdownRenderer replaces any <p> 
  with <span> (inline, no line breaks), but EQCaptionAndMarkSchemeMarkdownRenderer does not
- Creators therefore have to use <br> to add new lines in questions, but not in 
  captions/mark schemes

- For this reason we have to use the appropriate renderer for each content type outside 
  of creation (have renamed them to make this more obvious)

*/

const renderer = new Renderer();
renderer.paragraph = text => `<span data-notranslate>${text}</span>`;

type Props = {
  markdown: string;
  disableTooltips?: boolean;
};

export default function EQQuestionMarkdownRenderer(props: Props) {
  const { markdown, disableTooltips } = props;

  const tooltipBoundingElementRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const options = {
    replace: (domNode: any) => {
      if (domNode.attribs?.id in KEYWORDS) {
        return (
          <div style={{ display: "inline-flex", cursor: "help" }}>
            <Tooltip
              tooltip={KEYWORDS[domNode.attribs.id].tooltip}
              contrast="high"
              maxWidth="260px"
              boundingElementRef={tooltipBoundingElementRef}
              position="bottom"
            >
              <span
                style={{
                  textDecoration: "underline",
                  textDecorationStyle: "dashed",
                  textDecorationColor: theme["primary-low-content"],
                  textUnderlineOffset: "3px",
                  textDecorationSkipInk: "none"
                }}
              >
                {domNode.children[0].data}
              </span>
            </Tooltip>
          </div>
        );
      }

      if (domNode.attribs?.class === KATEX_CLASS) {
        return <InlineMath math={domNode.children[0].data} />;
      }
    }
  };

  const markdownWithoutHeadings = removeMarkdownHeadings(markdown);
  const toRender = retainWhiteSpace(markdownWithoutHeadings);
  const markedUp = marked(toRender, { renderer });

  const replacer: MarkdownReplacer = useMemo(() => new MarkdownReplacer(), []);

  const replacedMarkUp = replacer
    .input(markedUp)
    .replaceKeywords((capture1, capture2, capture3) =>
      !disableTooltips
        ? `${capture1}<div id="${
            KEYWORDS[capture2.toLowerCase()].id
          }"/>${capture2}</div>${capture3}`
        : `${capture1}${capture2}${capture3}`
    )
    .replaceLatex(
      text => `<span class="${KATEX_CLASS}" data-notranslate>${text}</span>`
    );

  const sanitized = DOMPurify.sanitize(replacedMarkUp as any).toString();
  const parsed = Parser(sanitized, options);

  return (
    <div ref={tooltipBoundingElementRef} className="MarkdownRenderer">
      <Text lineHeight="large" dataNoTranslate>
        {parsed}
      </Text>
    </div>
  );
}
